import resolve from '../../../utils/object/resolve.js'

/**
 * Evaluates a conditional expression against data
 * Supports && and || operators, comparison operators (==, !=, >, <, >=, <=)
 * @param {string} condition - The condition string to evaluate
 * @param {object} data - The data object to evaluate against
 * @returns {boolean} - The result of the condition evaluation
 */
const evaluateCondition = (condition, data) => {
  if (!condition || !data) return false

  // Handle || operator (OR logic)
  if (condition.includes('||')) {
    const orParts = condition.split('||').map(part => part.trim())
    return orParts.some(part => evaluateCondition(part, data))
  }

  // Handle && operator (AND logic)
  if (condition.includes('&&')) {
    const andParts = condition.split('&&').map(part => part.trim())
    return andParts.every(part => evaluateCondition(part, data))
  }

  // Handle individual comparison
  return evaluateComparison(condition, data)
}

/**
 * Evaluates a single comparison expression
 * @param {string} comparison - The comparison string (e.g., "parentId != null")
 * @param {object} data - The data object to evaluate against
 * @returns {boolean} - The result of the comparison
 */
const evaluateComparison = (comparison, data) => {
  // Match comparison operators
  const operators = ['!=', '==', '>=', '<=', '>', '<']
  let operator = null
  let leftSide = ''
  let rightSide = ''

  for (const op of operators) {
    if (comparison.includes(op)) {
      operator = op
      const parts = comparison.split(op).map(part => part.trim())
      leftSide = parts[0]
      rightSide = parts[1]
      break
    }
  }

  if (!operator) {
    console.warn(`Invalid comparison format: ${comparison}`)
    return false
  }

  // Resolve values
  const leftValue = resolveValue(leftSide, data)
  const rightValue = resolveValue(rightSide, data)

  // Perform comparison
  switch (operator) {
    case '==':
      return leftValue == rightValue
    case '!=':
      return leftValue != rightValue
    case '>':
      return leftValue > rightValue
    case '<':
      return leftValue < rightValue
    case '>=':
      return leftValue >= rightValue
    case '<=':
      return leftValue <= rightValue
    default:
      return false
  }
}

/**
 * Resolves a value from the data or returns literal value
 * @param {string} value - The value to resolve
 * @param {object} data - The data object to resolve from
 * @returns {any} - The resolved value
 */
const resolveValue = (value, data) => {
  if (value === 'null') return null
  if (value === 'undefined') return undefined
  if (value === 'true') return true
  if (value === 'false') return false
  
  // Check if it's a number
  if (!isNaN(value) && !isNaN(parseFloat(value))) {
    return parseFloat(value)
  }

  // Check if it's a quoted string
  if ((value.startsWith('"') && value.endsWith('"')) || 
      (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1)
  }

  // Try to resolve as a field path
  const resolvedValue = resolve(value, data)
  // Return the resolved value, even if it's undefined
  return resolvedValue
}

/**
 * Processes a formula string and substitutes variables with actual values
 * @param {string} formula - The formula string with ${variable} placeholders
 * @param {object} data - The data object to resolve variables from
 * @returns {string} - The processed formula result
 */
const processFormula = (formula, data) => {
  if (!formula) return ''

  // If formula is a single ${variable}, return the raw resolved value (preserves Date, number, etc.)
  const singleVarMatch = /^\$\{([^}]+)\}$/.exec(formula)
  if (singleVarMatch) {
    return resolve(formula, data)
  }

  // Multi-part formula: substitute all ${variable} placeholders with string values
  const variablePattern = /\$\{([^}]+)\}/g
  let result = formula
  let match

  while ((match = variablePattern.exec(formula)) !== null) {
    // Pass full match[0] (e.g. '${now}') so resolve special cases trigger correctly
    const variableValue = resolve(match[0], data)
    const replacementValue = variableValue !== undefined && variableValue !== null
      ? String(variableValue)
      : ''
    result = result.replace(match[0], replacementValue)
  }

  return result
}

/**
 * Header CalcField operation - evaluates rules with conditions and formulas
 * @param {object} data - The data object to operate on
 * @param {object} operation - The operation configuration
 * @returns {object} - The updated data object
 */
const headerCalcField = (data, operation) => {
  const { targetField, rules } = operation

  if (!data || !targetField || !rules || !Array.isArray(rules)) {
    console.warn('Invalid headerCalcField operation configuration')
    return data
  }

  // Find the first rule that matches the condition
  for (const rule of rules) {
    const { when, formula } = rule

    if (!when || !formula) {
      console.warn('Invalid rule configuration - missing when or formula')
      continue
    }

    // Evaluate the condition
    if (evaluateCondition(when, data)) {
      // Process the formula and get the result
      const calculatedValue = processFormula(formula, data)
      
      // Use resolve to set the value at the target field path
      // For simple field names, just set directly
      if (targetField.includes('.')) {
        // Complex path - would need a setter utility, for now just warn
        console.warn(`Complex target field paths not yet supported: ${targetField}`)
        return {
          ...data,
          [targetField]: calculatedValue
        }
      } else {
        // Simple field name
        return {
          ...data,
          [targetField]: calculatedValue
        }
      }
    }
  }

  // No rule matched, return original data
  return data
}

export default headerCalcField
